import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEvents } from '../hooks/useEvents';
import InviteWelcomeModal from '../components/InviteWelcomeModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
import Dashboard from './Dashboard';
import EventDetail from './EventDetail';
import HistoryView from './History';
import GreatestHits from './GreatestHits';
import EventsView from './Events';
import ProfileView from './Profile';

const STYLES = `
:root{--clay:#C4612A;--clay-light:#FAEEE8;--clay-dark:#8B3A12;--bark:#2C1A0E;--bark-mid:#5A3E2B;--bark-soft:#7A6555;--parchment:#FAF7F2;--parchment-mid:#F0EAE0;--parchment-dark:#E5D9C8;--cream:#FFFDF9;--sage:#5B7A5B;--sage-light:#EBF2EB;--gold:#D4A017;--gold-light:#FDF6E3;--sidebar-w:260px;--mobile-nav-h:64px;}
.app-shell{display:flex;height:100vh;overflow:hidden;}
.sidebar{width:var(--sidebar-w);background:var(--bark);color:var(--parchment);display:flex;flex-direction:column;flex-shrink:0;overflow-y:auto;}
.main-area{flex:1;overflow-y:auto;background:var(--parchment);}
.sidebar-brand{padding:28px 22px 18px;border-bottom:1px solid rgba(250,247,242,.1);}
.sidebar-brand h1{font-family:'Cormorant Garamond',serif;font-size:21px;line-height:1.25;color:var(--parchment);}
.sidebar-brand .tagline{font-size:11px;color:rgba(250,247,242,.4);letter-spacing:.1em;text-transform:uppercase;margin-top:5px;}
.nav-group-label{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:rgba(250,247,242,.35);padding:14px 22px 5px;}
.nav-btn{display:flex;align-items:center;gap:11px;width:calc(100% - 16px);padding:10px 16px 10px 18px;background:none;border:none;color:rgba(250,247,242,.7);font-family:'Jost',sans-serif;font-size:14px;cursor:pointer;border-radius:10px;margin:1px 8px;transition:all .15s;text-align:left;white-space:nowrap;overflow:hidden;}
.nav-btn:hover{background:rgba(250,247,242,.07);color:var(--parchment);}
.nav-btn.active{background:var(--clay);color:white;}
.nav-icon{font-size:16px;width:20px;text-align:center;flex-shrink:0;}
.nav-badge{margin-left:auto;background:rgba(255,255,255,.2);color:white;font-size:10px;padding:2px 7px;border-radius:12px;font-weight:500;flex-shrink:0;}
.nav-btn.active .nav-badge{background:rgba(255,255,255,.25);}
.sidebar-footer{margin-top:auto;padding:16px;border-top:1px solid rgba(250,247,242,.1);display:flex;align-items:center;gap:10px;}
.user-name{font-size:13px;font-weight:500;color:var(--parchment);}
.user-role{font-size:11px;color:rgba(250,247,242,.4);}
.mobile-nav{display:none;position:fixed;bottom:0;left:0;right:0;height:var(--mobile-nav-h);background:var(--bark);border-top:1px solid rgba(250,247,242,.1);z-index:200;justify-content:space-around;align-items:center;padding:0 8px;}
.mob-nav-btn{display:flex;flex-direction:column;align-items:center;gap:3px;background:none;border:none;color:rgba(250,247,242,.5);cursor:pointer;padding:8px 12px;border-radius:10px;transition:all .15s;font-family:'Jost',sans-serif;}
.mob-nav-btn.active{color:var(--clay);}
.mob-icon{font-size:20px;}
.mob-label{font-size:10px;letter-spacing:.03em;}
.avatar{border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:500;flex-shrink:0;}
@media(max-width:768px){.sidebar{display:none;}.mobile-nav{display:flex;}.main-area{padding-bottom:var(--mobile-nav-h);}}
@keyframes bellPulse{0%,100%{opacity:1;transform:scale(1);}50%{opacity:.5;transform:scale(.9);}}
`;

const NAV = [
  { id:'dashboard', label:'Home',          icon:'🏠' },
  { id:'events',    label:'Events',         icon:'📅' },
  { id:'history',   label:'History',        icon:'📚' },
  { id:'hits',      label:'Greatest Hits',  icon:'🌟' },
  { id:'profile',   label:'My Profile',     icon:'👤' },
];

const AVATAR_COLORS = ['#C4612A','#5D4037','#1A237E','#1B5E20','#BF360C','#4A148C'];

function timeAgo(date) {
  const mins = Math.floor((Date.now() - new Date(date)) / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs/24)}d ago`;
}

export default function Main({ inviteEventId }) {
  const { profile, signOut, token } = useAuth();
  const eventsHook = useEvents();
  const { events } = eventsHook;
  const [view, setView]         = useState('dashboard');
  const [eventId, setEventId]   = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(() => !!sessionStorage.getItem('inviteEventId'));
  const [inviteEvent, setInviteEvent]         = useState(null);
  const stableInviteEventId = useState(() => sessionStorage.getItem('inviteEventId'))[0];
  console.log('Main.js — showInviteModal:', showInviteModal, 'stableInviteEventId:', stableInviteEventId, 'inviteEvent:', inviteEvent);
  const [notifications, setNotifications] = useState([]);
  const [openBellId, setOpenBellId]       = useState(null);
  const bellRef                           = useRef(null);

  function nav(v, id) {
    setView(v);
    if (id !== undefined) {
      setEventId(id);
      if (id && v === 'event') markEventNotificationsRead(id);
    }
  }

 useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (stableInviteEventId) fetchInviteEvent();
  }, []);

  async function fetchInviteEvent() {
    try {
      const res = await fetch(`${API}/events/${stableInviteEventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setInviteEvent(await res.json());
    } catch(e) { console.error(e); }
  }

  useEffect(() => {
    function handleClickOutside(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setOpenBellId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function fetchNotifications() {
    try {
      const res = await fetch(`${API}/profiles/me/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) setNotifications(data);
    } catch(e) { console.error(e); }
  }

  async function markEventNotificationsRead(evId) {
    const unread = notifications.filter(n => n.data?.event_id === evId && !n.read);
    await Promise.all(unread.map(n =>
      fetch(`${API}/profiles/me/notifications/${n.id}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      })
    ));
    setNotifications(prev => prev.map(n =>
      n.data?.event_id === evId ? { ...n, read: true } : n
    ));
  }

  async function markAllEventNotificationsRead(evId) {
    await markEventNotificationsRead(evId);
    setOpenBellId(null);
  }

  function getEventNotifications(evId) {
    return notifications.filter(n => n.data?.event_id === evId);
  }

  function getUnreadCount(evId) {
    return notifications.filter(n => n.data?.event_id === evId && !n.read).length;
  }
  const pendingCount = events
    .filter(e => e.host_id === profile?.id)
    .flatMap(e => (e.dishes || []).filter(d => d.status === 'pending'))
    .length;

  const upcomingMine = events.filter(e =>
    e.status === 'upcoming' && (e.host_id === profile?.id || (e.event_guests||[]).some(g => g.user_id === profile?.id))
  );

  const currentEvent = events.find(e => e.id === eventId);

  const initials = profile?.full_name?.split(' ').map(w=>w[0]).join('').toUpperCase() || '?';
  const avatarColor = AVATAR_COLORS[profile?.id?.charCodeAt(0) % AVATAR_COLORS.length] || '#C4612A';

  return (
    <>
      <style>{STYLES}</style>
      <div className="app-shell">
        <aside className="sidebar">
          <div className="sidebar-brand">
            <h1>What can<br/>we bring?</h1>
            <div className="tagline">Dinner party planner</div>
          </div>
          <nav style={{padding:'8px',flex:1}}>
            <div className="nav-group-label">Navigate</div>
            {NAV.map(n => (
              <button key={n.id} className={`nav-btn ${view===n.id&&!eventId?'active':''}`} onClick={() => nav(n.id, null)}>
                <span className="nav-icon">{n.icon}</span>
                {n.label}
                {n.id==='dashboard' && pendingCount>0 && <span className="nav-badge">{pendingCount}</span>}
              </button>
            ))}
            {upcomingMine.length > 0 && <>
              <div className="nav-group-label" style={{marginTop:8}}>My events</div>
             {upcomingMine.map(e => {
              const evPending   = (e.dishes||[]).filter(d=>d.status==='pending').length;
              const unreadCount = getUnreadCount(e.id);
              const evNotifs    = getEventNotifications(e.id);
              const isOpen      = openBellId === e.id;

              return (
                <div key={e.id} style={{position:'relative'}} ref={isOpen ? bellRef : null}>
                  <div style={{display:'flex',alignItems:'center',gap:4}}>
                    <button
                      className={`nav-btn ${eventId===e.id?'active':''}`}
                      style={{flex:1,margin:0,width:'auto'}}
                      onClick={() => nav('event', e.id)}>
                      <span className="nav-icon">{e.emoji}</span>
                      <span style={{overflow:'hidden',textOverflow:'ellipsis',flex:1}}>{e.title}</span>
                    </button>

                    {unreadCount > 0 && (
                     <button
                        onClick={ev => { ev.stopPropagation(); setOpenBellId(isOpen ? null : e.id); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 6px',
                          borderRadius: 6,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: '#C4612A',
                          color: 'white',
                          fontSize: 10,
                          fontWeight: 500,
                          fontFamily: 'Jost,sans-serif',
                          animation: 'bellPulse 1.5s ease-in-out infinite',
                        }}>
                          {unreadCount}
                        </span>
                      </button>
                    )}
                  </div>

                  {/* Notification dropdown */}
                {isOpen && (
                    <div style={{
                      position: 'fixed',
                      left: 228,
                      top: 'auto',
                      width: 300,
                      background: 'white',
                      borderRadius: 14,
                      border: '1px solid #E5D9C8',
                      boxShadow: '0 8px 32px rgba(44,26,14,.18)',
                      zIndex: 400,
                      overflow: 'hidden',
                    }}>
                      <div style={{padding:'14px 16px 10px',borderBottom:'1px solid #E5D9C8',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                        <p style={{fontFamily:'Cormorant Garamond,serif',fontSize:16,color:'#2C1A0E'}}>Notifications</p>
                        <button
                          onClick={() => markAllEventNotificationsRead(e.id)}
                          style={{background:'none',border:'none',fontSize:11,color:'#C4612A',cursor:'pointer',fontFamily:'Jost,sans-serif',fontWeight:500}}>
                          Mark all read
                        </button>
                      </div>
                      <div style={{maxHeight:320,overflowY:'auto'}}>
                        {evNotifs.length === 0 && (
                          <p style={{fontSize:13,color:'#7A6555',padding:'16px',textAlign:'center'}}>No notifications</p>
                        )}
                        {evNotifs.map(n => (
                          <div key={n.id} style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #F0EAE0',
                            background: n.read ? 'white' : '#FAEEE8',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            nav('event', e.id);
                            markEventNotificationsRead(e.id);
                            setOpenBellId(null);
                          }}>
                            <p style={{fontSize:13,fontWeight:500,color:'#2C1A0E',marginBottom:2}}>{n.title}</p>
                            {n.body && <p style={{fontSize:12,color:'#7A6555',lineHeight:1.5}}>{n.body}</p>}
                            <p style={{fontSize:10,color:'#7A6555',marginTop:4}}>{timeAgo(n.created_at)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            </>}
          </nav>
          <div className="sidebar-footer">
            <div className="avatar" style={{width:36,height:36,background:avatarColor,fontSize:13,color:'white'}}>{initials}</div>
            <div style={{flex:1,minWidth:0}}>
              <div className="user-name">{profile?.full_name || 'You'}</div>
              <div className="user-role">{(profile?.dietary||[]).join(', ') || 'No dietary notes'}</div>
            </div>
            <button onClick={signOut} style={{background:'none',border:'none',color:'rgba(250,247,242,.4)',cursor:'pointer',fontSize:18}} title="Sign out">↪</button>
          </div>
        </aside>

        <main className="main-area">
          {view==='event' && currentEvent
            ? <EventDetail event={currentEvent} onBack={() => nav('dashboard', null)} eventsHook={eventsHook} />
            : view==='dashboard' ? <Dashboard events={events} loading={eventsHook.loading} onNavigate={nav} onCreate={eventsHook.createEvent} profile={profile} />
            : view==='events'    ? <EventsView events={events} onNavigate={nav} profile={profile} />
            : view==='history'   ? <HistoryView events={events} onNavigate={nav} />
            : view==='hits'      ? <GreatestHits events={events} />
	    : view==='profile'  ? <ProfileView />
            : null}
        </main>

        <nav className="mobile-nav">
          {NAV.map(n => (
            <button key={n.id} className={`mob-nav-btn ${view===n.id&&!eventId?'active':''}`} onClick={() => nav(n.id, null)}>
              <span className="mob-icon">{n.icon}</span>
              <span className="mob-label">{n.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {showInviteModal && inviteEvent && (
        <InviteWelcomeModal
          event={inviteEvent}
          onClose={() => {
            setShowInviteModal(false);
            sessionStorage.removeItem('inviteEventId');
            window.history.replaceState({}, '', '/');
          }}
          onAccepted={() => {
            setShowInviteModal(false);
            sessionStorage.removeItem('inviteEventId');
            window.history.replaceState({}, '', '/');
            nav('event', stableInviteEventId);
          }}
        />
      )}
    </>
  );
}
